import React, { useState, useEffect } from 'react';
import apiClient from '../../../api/client';
import { ProteinTarget } from '../../../utils/patentZones';

interface TargetLink {
  id: number;
  biologic_id: number;
  interaction_type: string;
  affinity_value?: number;
  affinity_unit?: string;
  binding_site?: string;
  species?: string;
  relationship_type?: string;
  target_name?: string;
  uniprot_id?: string;
  sequence_type?: string;
}

interface TargetsTabProps {
  smiles: string;
  moleculeId?: number;
}

const TargetsTab: React.FC<TargetsTabProps> = ({ smiles, moleculeId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discoveredTargets, setDiscoveredTargets] = useState<ProteinTarget[]>([]);
  const [userTargets, setUserTargets] = useState<TargetLink[]>([]);
  const [fetched, setFetched] = useState(false);

  // Sort state for discovered targets
  const [sortKey, setSortKey] = useState<'name' | 'activity' | 'type'>('activity');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    if (!smiles || fetched) return;

    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const promises: Promise<any>[] = [
          apiClient.get('/enrich/compound-targets', { params: { smiles }, timeout: 10000 }),
        ];
        if (moleculeId) {
          promises.push(
            apiClient.get(`/molecules/${moleculeId}/targets`).catch(() => ({ data: [] }))
          );
        }

        const results = await Promise.all(promises);

        // Discovered targets from ChEMBL
        const enrichData = results[0].data;
        const mapped: ProteinTarget[] = (enrichData.targets || []).map((t: any) => ({
          target_name: t.target_name || t.pref_name || 'Unknown Target',
          uniprot_id: t.uniprot_id || t.accession || undefined,
          chembl_id: t.chembl_id || undefined,
          activity_type: t.activity_type || t.standard_type || undefined,
          activity_value: t.activity_value || t.standard_value || undefined,
          activity_units: t.activity_units || t.standard_units || undefined,
          assay_type: t.assay_type || undefined,
          source: t.source || 'ChEMBL',
        }));
        setDiscoveredTargets(mapped);

        // User-curated targets
        if (results[1]) {
          const links: TargetLink[] = Array.isArray(results[1].data) ? results[1].data : [];
          setUserTargets(links);
        }

        setFetched(true);
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message || 'Failed to load targets');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [smiles, moleculeId, fetched]);

  const sortedDiscovered = React.useMemo(() => {
    const arr = [...discoveredTargets];
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') {
        cmp = (a.target_name || '').localeCompare(b.target_name || '');
      } else if (sortKey === 'activity') {
        const va = a.activity_value ?? Infinity;
        const vb = b.activity_value ?? Infinity;
        cmp = va - vb;
      } else {
        cmp = (a.activity_type || '').localeCompare(b.activity_type || '');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [discoveredTargets, sortKey, sortDir]);

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div className="w-6 h-6 border-2 border-t-teal-500 rounded-full animate-spin" style={{ borderColor: 'var(--card-border)', borderTopColor: '#14b8a6' }} />
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Searching ChEMBL bioactivity data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)' }}>
        <p className="text-sm" style={{ color: '#ef4444' }}>{error}</p>
      </div>
    );
  }

  const totalTargets = userTargets.length + discoveredTargets.length;

  return (
    <div className="space-y-5">
      {/* ── Section 1: Your Targets ── */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <svg className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
            Your Targets
          </h4>
        </div>

        {userTargets.length === 0 ? (
          <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--card-border)' }}>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              No targets added yet. Add targets manually or they'll be discovered automatically through compound enrichment.
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {userTargets.map((t) => (
              <div key={t.id} className="p-2.5 rounded" style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--card-border)' }}>
                <div className="flex items-start justify-between">
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    {t.target_name || `Target #${t.biologic_id}`}
                  </span>
                  {t.affinity_value != null && (
                    <span className="text-xs font-mono flex-shrink-0 ml-2" style={{ color: '#0d9488' }}>
                      {t.interaction_type}: {t.affinity_value} {t.affinity_unit || 'nM'}
                    </span>
                  )}
                </div>
                <div className="flex gap-3 mt-1 flex-wrap">
                  {t.uniprot_id && (
                    <a
                      href={`https://www.uniprot.org/uniprot/${t.uniprot_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs hover:underline"
                      style={{ color: '#0d9488' }}
                    >
                      UniProt: {t.uniprot_id}
                    </a>
                  )}
                  {t.relationship_type && (
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t.relationship_type}</span>
                  )}
                  {t.binding_site && t.binding_site !== 'Unknown' && (
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Site: {t.binding_site}</span>
                  )}
                  {t.species && t.species !== 'Homo sapiens' && (
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t.species}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Divider ── */}
      <div style={{ borderTop: '1px solid var(--card-border)' }} />

      {/* ── Section 2: Discovered Targets ── */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <svg className="w-4 h-4" style={{ color: '#0d9488' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
            Discovered Targets
          </h4>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>({discoveredTargets.length})</span>
        </div>
        <p className="text-[10px] mb-3" style={{ color: 'var(--text-muted)' }}>
          Automatically identified from ChEMBL bioactivity data
        </p>

        {discoveredTargets.length === 0 && fetched ? (
          <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--card-border)' }}>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              No known protein targets found. Target discovery runs automatically when compounds are enriched.
            </p>
          </div>
        ) : (
          <>
            {/* Sort controls */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Sort:</span>
              {(['activity', 'name', 'type'] as const).map(k => (
                <button
                  key={k}
                  onClick={() => handleSort(k)}
                  className="text-[10px] px-1.5 py-0.5 rounded"
                  style={{
                    color: sortKey === k ? '#0d9488' : 'var(--text-muted)',
                    backgroundColor: sortKey === k ? 'rgba(13,148,136,0.1)' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {k === 'activity' ? 'Potency' : k === 'name' ? 'Name' : 'Type'}
                  {sortKey === k && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                </button>
              ))}
            </div>

            {/* Target table */}
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {sortedDiscovered.map((target, idx) => (
                <div key={idx} className="p-2.5 rounded transition-colors" style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--card-border)' }}>
                  <div className="flex items-start justify-between">
                    <span className="text-sm font-medium leading-tight" style={{ color: 'var(--text-primary)' }}>
                      {target.target_name}
                    </span>
                    {target.activity_value != null && (
                      <span className="text-xs font-mono flex-shrink-0 ml-2" style={{ color: '#0d9488' }}>
                        {target.activity_type}: {Number(target.activity_value).toFixed(1)} {target.activity_units || 'nM'}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-3 mt-1 flex-wrap">
                    {target.uniprot_id && (
                      <a
                        href={`https://www.uniprot.org/uniprot/${target.uniprot_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs hover:underline"
                        style={{ color: '#0d9488' }}
                      >
                        UniProt: {target.uniprot_id}
                      </a>
                    )}
                    {target.chembl_id && (
                      <a
                        href={`https://www.ebi.ac.uk/chembl/target_report_card/${target.chembl_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs hover:underline"
                        style={{ color: 'var(--text-muted)' }}
                      >
                        {target.chembl_id}
                      </a>
                    )}
                    {target.assay_type && (
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Assay: {target.assay_type}</span>
                    )}
                    {target.source && target.source !== 'ChEMBL' && (
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{target.source}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Total summary */}
      {fetched && totalTargets > 0 && (
        <div className="text-center pt-1">
          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            {totalTargets} target{totalTargets !== 1 ? 's' : ''} total
            {userTargets.length > 0 && ` (${userTargets.length} curated, ${discoveredTargets.length} discovered)`}
          </span>
        </div>
      )}
    </div>
  );
};

export default TargetsTab;
